import React, { useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';

const Canvas = styled('canvas')({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 0,
});

interface Node {
  x: number;
  y: number;
  radius: number;
  vx: number;
  vy: number;
  colorIndex: number;
}

const BackgroundAnimation: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    // Animation parameters
    const nodes: Node[] = [];
    const colors = {
      purple: '#8A2BE2',    // Pulse Purple
      orange: '#FF6F00',    // Energy Orange
      blue: '#003366',      // Eterna Blue
      green: '#00CC99',     // Aurora Green
      white: '#F5F5F5',     // Ethereal White
      darkGray: '#4B4B4B',  // Graphite Gray
      lightGray: '#D9D9D9', // Cloud Gray
    };

    // Create nodes
    for (let i = 0; i < 50; i++) {
      nodes.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 2 + 1,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
        colorIndex: Math.floor(Math.random() * 3), // For color variation
      });
    }

    // Animation loop
    const animate = () => {
      ctx.fillStyle = 'rgba(17, 17, 25, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      nodes.forEach((node, i) => {
        // Update position
        node.x += node.vx;
        node.y += node.vy;

        // Bounce off walls
        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;

        // Create different gradient combinations based on colorIndex
        const gradient = ctx.createRadialGradient(
          node.x, node.y, 0,
          node.x, node.y, node.radius * 3
        );

        // Maintain purple as primary but mix with other colors
        if (node.colorIndex === 0) {
          gradient.addColorStop(0, colors.purple);
          gradient.addColorStop(0.5, colors.blue);
          gradient.addColorStop(1, colors.green);
        } else if (node.colorIndex === 1) {
          gradient.addColorStop(0, colors.purple);
          gradient.addColorStop(0.5, colors.orange);
          gradient.addColorStop(1, colors.white);
        } else {
          gradient.addColorStop(0, colors.purple);
          gradient.addColorStop(0.5, colors.green);
          gradient.addColorStop(1, colors.blue);
        }

        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();

        // Connect nearby nodes with dynamic colors
        nodes.slice(i + 1).forEach((otherNode) => {
          const dx = node.x - otherNode.x;
          const dy = node.y - otherNode.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 100) {
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(otherNode.x, otherNode.y);
            
            // Create gradient for connection lines
            const lineGradient = ctx.createLinearGradient(
              node.x, node.y, otherNode.x, otherNode.y
            );
            lineGradient.addColorStop(0, `rgba(138, 43, 226, ${1 - distance / 100})`); // Purple
            lineGradient.addColorStop(1, `rgba(0, 204, 153, ${1 - distance / 100})`);  // Green
            
            ctx.strokeStyle = lineGradient;
            ctx.stroke();
          }
        });
      });

      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', setCanvasSize);
    };
  }, []);

  return <Canvas ref={canvasRef} />;
};

export default BackgroundAnimation; 